import '@/app.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { isApiError } from '@/api/client'
import { useGlobalEvents } from '@/api/sse'
import { Toaster } from '@/components/ui/toast'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthGate } from '@/features/auth/AuthGate'
import { installStaleChunkReload } from '@/lib/staleChunk'
import { createAppRouter } from '@/router'
import { bindUiToDocument } from '@/stores/uiStore'

/**
 * Boot: query client → router → SSE bridge → toaster. Theme/density are
 * mirrored onto `<html>` before the first render.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      // 4xx answers are facts, not flakes; only retry network/5xx once.
      retry: (count, err) => count < 1 && !(isApiError(err) && err.status > 0 && err.status < 500),
    },
    mutations: { retry: 0 },
  },
})

const router = createAppRouter(queryClient)

function SseBridge() {
  useGlobalEvents(queryClient)
  return null
}

bindUiToDocument()
// A tab opened before a redeploy reloads itself when it asks for a chunk the new build deleted.
installStaleChunkReload()

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root missing from index.html')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={400}>
        <AuthGate>
          <SseBridge />
          <RouterProvider router={router} />
        </AuthGate>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
)
